import { FC, useContext, useEffect, useState } from 'react';
import { FieldArray, Form } from 'formik';
import clsx from 'clsx';
import {
  Autocomplete,
  Box,
  Button,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormHelperText,
  IconButton,
  ButtonGroup,
  Divider,
  FormLabel,
} from '@mui/material';
import { Loader, Modal } from '../../components';
import { IEditEmployeeMarginCategory, IEmployeeDetail, ILookupType } from '../../models/employee';
import { getLocalDate } from '../../helpers';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../styles';
import { Save, Close } from '@mui/icons-material';
import { EmployeesAPI } from '../../fetch/employee';
import { useToastContext } from '../../hooks';
import { IMPORTANT_ROLES } from './EditorModal';
import { DesktopDatePicker } from '@mui/lab';
import { SearchAheadDropdown } from './search-ahead-dropdown';
import { UserContext } from '../../context';
import { Dropdowns } from '../../helpers/formik-mui';
import { IMarginCategoryAPI } from '../../fetch/margin-category';
import { IDropdownResponse } from '../../models';
import { impersonateAndNavigateToFrankieProper } from '../../helpers/navigation';

interface IEmployeeModalContent {
  employeeId: string;
  open: boolean;
  handleClose: () => void;
  setFieldValue: (key: string, val: any) => void;
  values: any;
  handleSubmit: () => void;
  isSubmitting: boolean;
  dirty: boolean;
  isValid: boolean;
  touched: any;
  currentEmployee: IEmployeeDetail | undefined;
  subsidiaries: ILookupType[];
  bankAccounts: ILookupType[];
  roles: ILookupType[];
  offices: ILookupType[];
  activityTypes: ILookupType[];
  specialtyTypes: ILookupType[];
  productSpecialties: ILookupType[];
  teams: ILookupType[];
  errors: any;
  
  primaryBusinessUnitOverride: ILookupType[];
  departmentOverride: ILookupType[];
  productTypeOverride: ILookupType[];
  commissionTables: ILookupType[];
  isAdActiveMessage: string;
  fetchEmployeeData: () => void;
}

export const EmployeeModalContent: FC<IEmployeeModalContent> = ({
  employeeId,
  open,
  handleClose,
  setFieldValue,
  values,
  handleSubmit,
  isSubmitting,
  dirty,
  isValid,
  currentEmployee,
  subsidiaries,
  bankAccounts,
  roles,
  offices,
  activityTypes,
  specialtyTypes,
  productSpecialties,
  teams,
  errors,
  touched,
  primaryBusinessUnitOverride,
  departmentOverride,
  productTypeOverride,
  commissionTables,
  isAdActiveMessage: statusMessage,
  fetchEmployeeData,
}) => {
  const name = `${currentEmployee?.firstName ? `${currentEmployee?.firstName} ` : ''}${
    currentEmployee?.lastName ? currentEmployee?.lastName : ''
  }`;
  const classes = useStyles();

  // Feature flag for Support Team functionality
  const SUPPORT_TEAM_FEATURE_FLAG = 'admin-support-staff';
  const BULK_EMAIL_CREDIT_FLAG = 'action-list-email';

  const { userContext } = useContext(UserContext);
  const hasSupportTeamFeature = userContext?.featureFlags?.find((flag: any) => flag?.name === SUPPORT_TEAM_FEATURE_FLAG)?.hasAccess ?? false;
  const hasBulkEmailCreditFeature = userContext?.featureFlags?.find((flag: any) => flag?.name === BULK_EMAIL_CREDIT_FLAG)?.hasAccess ?? false;

  const [inputRolesValues, setInputRolesValues] = useState<ILookupType[]>(
    roles.filter(role => values.roles.includes(role.value))
  );
  const [inputRolesDisplayValues, setInputRolesDisplayValues] = useState('');
  const [inputOfficesValues, setInputOfficesValues] = useState<ILookupType[]>(
    offices.filter(office => values.offices.includes(office.value))
  );
  const [inputOfficesDisplayValues, setInputOfficesDisplayValues] = useState('');

  const [inputActivityValues, setInputActivityValues] = useState<ILookupType[]>(
    activityTypes.filter(activityType => values.activityTypes.includes(activityType.value))
  );
  const [inputActivityDisplayValues, setInputActivityDisplayValues] = useState('');

  const [inputTeamsValues, setInputTeamsValues] = useState<ILookupType[]>(
    teams.filter(team => values.teams.includes(team.value))
  );
  const [inputTeamsDisplayValues, setInputTeamsDisplayValues] = useState('');


  const { showToast } = useToastContext();

  // Loading Assistant Options
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [assistants, setAssistants] = useState<ILookupType[]>([]);
  const [assistantsHasLoaded, setAssistantsHasLoaded] = useState(false);
  
  // Assistant UI states

  const fetchAssistants = async () => {
    setLoadingAssistants(true);
    try {
      const assistants = await EmployeesAPI.fetchAssistants(employeeId);
      setAssistants(assistants);
      setAssistantsHasLoaded(true);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee assistants. Please try again.'
      );
    } finally {
      setLoadingAssistants(false);
    }
  };

  // Loading Support Team Options (formerly Support Staff)
  const [loadingSupportStaff, setLoadingSupportStaff] = useState(false);
  const [supportStaff, setSupportStaff] = useState<ILookupType[]>([]);
  const [supportStaffHasLoaded, setSupportStaffHasLoaded] = useState(false);
  
  // Support Team UI state


  const fetchSupportStaff = async () => {
    setLoadingSupportStaff(true);
    try {
      const supportStaff = await EmployeesAPI.fetchSupportStaff(employeeId);
      setSupportStaff(supportStaff);
      setSupportStaffHasLoaded(true);
    } catch (error: any) {
      showToast(
        'error',
  error?.detail || 'Error trying to retrieve Employee support team. Please try again.'
      );
    } finally {
      setLoadingSupportStaff(false);
    }
  };

  // Loading Team Admin Options
  const [loadingTeamAdmins, setLoadingTeamAdmins] = useState(false);
  const [teamAdmins, setTeamAdmins] = useState<ILookupType[]>([]);
  const [teamAdminsHasLoaded, setTeamAdminsHasLoaded] = useState(false);
  
  // Team Admin UI states

  const [agentsInput, setAgentsInput] = useState('');

  const fetchTeamAdmins = async () => {
    setLoadingTeamAdmins(true);
    try {
      const teamAdmins = await EmployeesAPI.fetchTeamAdmins(employeeId);
      setTeamAdmins(teamAdmins);
      setTeamAdminsHasLoaded(true);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee team admins. Please try again.'
      );
    } finally {
      setLoadingTeamAdmins(false);
    }
  };

  const [loadingMarginCategories, setLoadingMarginCategories] = useState(false);
  const [marginCategories, setMarginCategories] = useState<IDropdownResponse[]>([]);

  const fetchMarginCategories = async (isActiveRef: { current: boolean }) => {
    setLoadingMarginCategories(true);
    try {
      const marginCategories = await IMarginCategoryAPI.lookup();
      if (isActiveRef.current) {
        setMarginCategories(marginCategories);
      }
    } catch (error: any) {
      if (isActiveRef.current) {
        showToast(
          'error',
          error?.detail || 'Error trying to retrieve Margin Categories. Please try again.'
        );
      }
    } finally {
      if (isActiveRef.current) {
        setLoadingMarginCategories(false);
      }
    }
  };

  useEffect(() => {
    const isActiveRef = { current: true };
    if (open) fetchMarginCategories(isActiveRef);
    return () => {
      isActiveRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const employeeBeingEdited = {
    isAssistant: values.roles.includes(IMPORTANT_ROLES.ASSISTANT),
    isProducer: values.roles.includes(IMPORTANT_ROLES.PRODUCER),
    isSupportStaff: values.roles.includes(IMPORTANT_ROLES.SUPPORT_STAFF)
  };

  // values for dropdowns from Formik values
  const primaryAdminValue = assistants.find(assistant => 
    values.assistants?.some((asst: { employeeId: string | number; isPrimary: any; }) => 
      asst.employeeId === assistant.value && 
      asst.isPrimary
    )
  ) || null;

  const additionalAssistantsValue = assistants.filter(assistant => 
    values.assistants?.some((asst: { employeeId: string | number; isPrimary: any; }) => 
      asst.employeeId === assistant.value && 
      !asst.isPrimary
    )
  );

  const supportStaffValue = supportStaff.filter(staff => 
    values.supportStaff?.some((ss: { employeeId: string | number; }) => ss.employeeId === staff.value)
  );

  const assistantAgentsValue = teamAdmins.filter(agent => 
    values.agents.includes(agent.value)
  );

  const supportStaffAgentsValue = teamAdmins.filter(agent => 
    values.supportStaffAgents?.includes(agent.value)
  );

  const agentsValue = teamAdmins.filter(agent => 
    values.agents.includes(agent.value)
  );

  // Producer => assistants, Assistant => Agents
  useEffect(() => {
    if (employeeBeingEdited.isProducer && !assistantsHasLoaded) {
      fetchAssistants();
    }
    if (employeeBeingEdited.isProducer && !supportStaffHasLoaded) {
      fetchSupportStaff();
    }
    if ((employeeBeingEdited.isAssistant || employeeBeingEdited.isSupportStaff) && !teamAdminsHasLoaded) {
      fetchTeamAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeBeingEdited.isAssistant, employeeBeingEdited.isProducer, employeeBeingEdited.isSupportStaff]);

  useEffect(() => {
    if (teamAdminsHasLoaded) {
      const currentIds: number[] = values.supportStaffAgents || [];
      if (currentIds.length) {
        const validSet = new Set(teamAdmins.map(t => t.value));
        const invalidIds = currentIds.filter(id => !validSet.has(id));
        if (invalidIds.length) {
          const pruned = currentIds.filter(id => validSet.has(id));
          setFieldValue('supportStaffAgents', pruned);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamAdminsHasLoaded, teamAdmins]);

  // Enforce mutual exclusivity: remove any overlap (an ID cannot be in both agents and supportStaffAgents)
  useEffect(() => {
    const overlap = values.agents.filter((id: number) => values.supportStaffAgents?.includes(id));
    if (overlap.length) {
      setFieldValue('supportStaffAgents', values.supportStaffAgents.filter((id: number) => !overlap.includes(id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.agents]);

  useEffect(() => {
    const overlap = values.supportStaffAgents?.filter((id: number) => values.agents.includes(id)) || [];
    if (overlap.length) {
      setFieldValue('agents', values.agents.filter((id: number) => !overlap.includes(id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.supportStaffAgents]);

  useEffect(() => {
    if (!values.teams.includes(values.primaryTeam)) {
      setFieldValue('primaryTeam', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.teams, values.primaryTeam]);

  const hasProtectedClients =
    userContext?.featureFlags?.find((flag: any) => flag?.name === 'protected-clients-v2')
      ?.hasAccess ?? false;

  const showCompanyMergeSetting =
      userContext?.featureFlags?.find((flag: any) => flag?.name === 'company-merge-user-setting')
        ?.hasAccess ?? false;

  return (
    <Modal
      maxWidth="lg"
      open={open}
      onClose={() => {
        handleClose();
      }}
      additionalContent={
        <Button
          sx={theme => ({ marginLeft: theme.spacing(1) })}
          variant={'contained'}
          onClick={async () => {
            try {
              impersonateAndNavigateToFrankieProper(employeeId, '_blank');
            } catch (error: any) {
              showToast('error', error?.detail ?? 'Error impersonating, please try again.');
            }
          }}
        >
          Impersonate
        </Button>
      }
      title={name}
    >
      {isSubmitting && <Loader type="overlay" position="centered" />}
      <Fade in={open}>
        <Form onSubmit={handleSubmit} autoComplete="none">
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} className={classes.grid}>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Employee Number:</span> {employeeId}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>AAD Status:</span>{' '}
                {currentEmployee?.isAdActive ? 'Active' : 'Inactive'}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Hire Date:</span>{' '}
                {currentEmployee?.hireDate
                  ? getLocalDate(currentEmployee?.hireDate.toString())
                  : ''}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Title:</span> {currentEmployee?.title}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Worksite:</span> {currentEmployee?.worksite}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Office:</span> {currentEmployee?.office}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Department:</span> {currentEmployee?.department}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Primary Business Unit:</span>{' '}
                {currentEmployee?.primaryBusinessUnit}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Primary Product Type:</span>{' '}
                {currentEmployee?.primaryProductType ? currentEmployee?.primaryProductType : ''}
              </Typography>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Primary Specialty Type:</span>{' '}
                {currentEmployee?.primarySpecialtyType ? currentEmployee?.primarySpecialtyType : ''}
              </Typography>
            </Grid>
            <Grid item container spacing={1} xs={12} md={6} className={classes.grid}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id={'subsidiaryLabel'} htmlFor="subsidiary" className={classes.label}>
                    Subsidiary
                  </InputLabel>
                  <Select
                    name="subsidiary"
                    labelId="subsidiaryLabel"
                    id="subsidiary"
                    value={values.subsidiaryId}
                    onChange={({ target: { value } }) => setFieldValue('subsidiaryId', value)}
                  >
                    {subsidiaries &&
                      subsidiaries.map((role, index) => {
                        return (
                          <MenuItem key={`${role.value}`} value={role.value}>
                            {role.description}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    id={'bankAccountLabel'}
                    htmlFor="bankAccount"
                    className={classes.label}
                  >
                    Entity Bank Account
                  </InputLabel>
                  <Select
                    name="bankAccount"
                    labelId="bankAccountLabel"
                    id="bankAccount"
                    value={values.bankAccountId}
                    onChange={({ target: { value } }) => setFieldValue('bankAccountId', value)}
                  >
                    {bankAccounts &&
                      bankAccounts.map((role, index) => {
                        return (
                          <MenuItem key={`${role.value}`} value={role.value}>
                            {role.description}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    multiple
                    id="roles"
                    value={inputRolesValues}
                    onChange={(event, newValue: any) => {
                      setInputRolesValues(newValue);
                      setFieldValue(
                        'roles',
                        newValue.map((val: any) => val.value)
                      );
                    }}
                    inputValue={inputRolesDisplayValues}
                    onInputChange={(event, newInputValue) => {
                      setInputRolesDisplayValues(newInputValue);
                    }}
                    options={roles}
                    getOptionLabel={option => option.description}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    filterOptions={(options, state) => {
                      const input = state.inputValue.trim().toLowerCase();
                      if (!input) return options;
                      return options.filter(o => o.description.toLowerCase().includes(input));
                    }}
                    renderOption={(props, option: ILookupType) => {
                      return (
                        <li {...props} key={`${option.value}`}>
                          {option.description}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <TextField {...params} label={'Roles'} placeholder="Add Roles" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    id="offices"
                    multiple
                    value={inputOfficesValues}
                    onChange={(event, newValue: any) => {
                      setInputOfficesValues(newValue);
                      setFieldValue(
                        'offices',
                        newValue.map((val: any) => val.value)
                      );
                    }}
                    inputValue={inputOfficesDisplayValues}
                    onInputChange={(event, newInputValue) => {
                      setInputOfficesDisplayValues(newInputValue);
                    }}
                    options={offices}
                    getOptionLabel={option => option.description}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    filterOptions={(options, state) => {
                      const input = state.inputValue.trim().toLowerCase();
                      if (!input) return options;
                      return options.filter(o => o.description.toLowerCase().includes(input));
                    }}
                    renderOption={(props, option: ILookupType) => {
                      return (
                        <li {...props} key={`${option.value}`}>
                          {option.description}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <TextField {...params} label={'Offices'} placeholder="Add Offices" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    id="activityTypes"
                    multiple
                    value={inputActivityValues}
                    onChange={(event, newValue: any) => {
                      setInputActivityValues(newValue);
                      setFieldValue(
                        'activityTypes',
                        newValue.map((val: any) => val.value)
                      );
                    }}
                    inputValue={inputActivityDisplayValues}
                    onInputChange={(event, newInputValue) => {
                      setInputActivityDisplayValues(newInputValue);
                    }}
                    options={activityTypes}
                    getOptionLabel={option => option.description}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    filterOptions={(options, state) => {
                      const input = state.inputValue.trim().toLowerCase();
                      if (!input) return options;
                      return options.filter(o => o.description.toLowerCase().includes(input));
                    }}
                    renderOption={(props, option: ILookupType) => {
                      return (
                        <li {...props} key={`${option.value}`}>
                          {option.description}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={'Activity Types'}
                        placeholder="Add Activity Types"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    id={'prouductSpecialtyLabel'}
                    htmlFor="ProductSpecialty"
                    className={classes.label}
                  >
                    Primary Product Specialty Type
                  </InputLabel>
                  <Select
                    name="ProductSpecialty"
                    labelId="prouductSpecialtyLabel"
                    id="ProductSpecialty"
                    value={values.primaryProductSpecialtyType}
                    onChange={({ target: { value } }) =>
                      setFieldValue('primaryProductSpecialtyType', value)
                    }
                  >
                    {productSpecialties &&
                      productSpecialties.map((role, index) => {
                        return (
                          <MenuItem key={`${index}`} value={role.value}>
                            {role.description}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <TextField
                    fullWidth
                    variant="outlined"
                    autoComplete="nope"
                    label="NetSuite Id"
                    name="netsuiteId"
                    value={values.netsuiteId}
                    size="small"
                    onChange={e => {
                      setFieldValue('netsuiteId', e.target.value);
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    id={'primaryBusinessUnitOverride'}
                    htmlFor="primaryBusinessUnitOverride"
                    className={classes.label}
                  >
                    Primary Business Unit Override
                  </InputLabel>
                  <Select
                    name="primaryBusinessUnitOverride"
                    labelId="primaryBusinessUnitIdOverrideLabel"
                    id="primaryBusinessUnitOverride"
                    value={values.primaryBusinessUnitOverride}
                    onChange={({ target: { value } }) =>
                      setFieldValue('primaryBusinessUnitOverride', value)
                    }
                  >
                    <MenuItem key={`none`} value={''}>
                      None
                    </MenuItem>
                    {primaryBusinessUnitOverride?.map((item, index) => {
                      return (
                        <MenuItem key={`${item.value}`} value={item.value}>
                          {item.description}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    id={'departmentOverride'}
                    htmlFor="departmentOverride"
                    className={classes.label}
                  >
                    Primary Department Override
                  </InputLabel>
                  <Select
                    name="departmentOverride"
                    labelId="deparmentOverrideLabel"
                    id="departmentOverride"
                    value={values.departmentOverride}
                    onChange={({ target: { value } }) =>
                      setFieldValue('departmentOverride', value)
                    }
                  >
                    <MenuItem key={`none`} value={''}>
                      None
                    </MenuItem>
                    {departmentOverride?.map((item, index) => {
                      return (
                        <MenuItem key={`${item.value}`} value={item.value}>
                          {item.description}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    id={'primaryProductTypeOverrideLabel'}
                    htmlFor="primaryProductTypeOverride"
                    className={classes.label}
                  >
                    Product Type Override
                  </InputLabel>

                  <Select
                    name="primaryProductTypeOverride"
                    labelId="primaryProductTypeOverrideLabel"
                    id="primaryProductTypeOverride"
                    value={values.primaryProductTypeOverride}
                    onChange={({ target: { value } }) =>
                      setFieldValue('primaryProductTypeOverride', value)
                    }
                  >
                    <MenuItem key={`none`} value={''}>
                      None
                    </MenuItem>
                    {productTypeOverride?.map((item, index) => {
                      return (
                        <MenuItem key={`${item.value}`} value={item.value}>
                          {item.description}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    id={'primarySpecialtyTypeOverrideLabel'}
                    htmlFor="primarySpecialtyTypeOverride"
                    className={classes.label}
                  >
                    Specialty Type Override
                  </InputLabel>
                  <Select
                    name="primarySpecialtyTypeOverride"
                    labelId="primarySpecialtyTypeOverrideLabel"
                    id="primarySpecialtyTypeOverride"
                    value={values.primarySpecialtyTypeOverride}
                    onChange={({ target: { value } }) =>
                      setFieldValue('primarySpecialtyTypeOverride', value)
                    }
                  >
                    <MenuItem key={`none`} value={''}>
                      None
                    </MenuItem>
                    {/* Activity Types are  */}
                    {specialtyTypes?.map((item, index) => {
                      return (
                        <MenuItem key={`${item.value}`} value={item.value}>
                          {item.description}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    id={'commissionTableLabel'}
                    htmlFor="commissionTable"
                    className={classes.label}
                  >
                    Commission Table
                  </InputLabel>
                  <Select
                    name="commissionTable"
                    labelId="commissionTableLabel"
                    id="commissionTable"
                    value={values.commissionTable}
                    onChange={({ target: { value } }) => setFieldValue('commissionTable', value)}
                  >
                    <MenuItem key={`none`} value={''}>
                      None
                    </MenuItem>
                    {/* Activity Types are  */}
                    {commissionTables?.map((item, index) => {
                      return (
                        <MenuItem key={`${item.value}`} value={item.value}>
                          {item.description}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Divider />
            <Grid item container spacing={1} xs={12}>
              <Grid item xs={12}>
                <FormControl
                  sx={{ flexDirection: 'row', alignItems: 'center' }}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <FormLabel
                    sx={theme => ({ marginRight: theme.spacing(0.5) })}
                    id={'isAdActiveLabel'}
                    htmlFor="isAdActive"
                    className={classes.label}
                  >
                    Access Is
                  </FormLabel>
                  <ButtonGroup id={'isAdActive'}>
                    <Button
                      variant={values.isAdActive ? 'contained' : 'outlined'}
                      onClick={() => setFieldValue('isAdActive', true)}
                    >
                      Active
                    </Button>
                    <Button
                      variant={values.isAdActive ? 'outlined' : 'contained'}
                      onClick={() => setFieldValue('isAdActive', false)}
                    >
                      Inactive
                    </Button>
                  </ButtonGroup>
                </FormControl>
              </Grid>
              <Grid item xs={6} className={classes.resetButtonContainer}>
                <FormControl
                  sx={{ flexDirection: 'row', alignItems: 'center' }}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <FormLabel
                    sx={theme => ({ marginRight: theme.spacing(0.5) })}
                    id={'isAdActiveLabel'}
                    htmlFor="isAdActive"
                    className={classes.label}
                  >
                    {statusMessage}
                  </FormLabel>
                  {currentEmployee?.overrideStatus && (
                    <Button
                      sx={theme => ({ marginLeft: theme.spacing(1) })}
                      variant={'contained'}
                      onClick={async () => {
                        try {
                          await EmployeesAPI.postResetOverride(employeeId);
                          await fetchEmployeeData();
                        } catch (error: any) {
                          showToast(
                            'error',
                            error?.detail ?? 'Error resetting employee override, please try again.'
                          );
                        }
                      }}
                    >
                      Reset
                    </Button>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          
          {/* Teams Section */}
          <Grid item container spacing={1} xs={12} className={classes.grid}>
            <Grid item xs={12}>
              <Typography className={clsx(classes.header, classes.noButton)} variant="body1">
                Teams
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  id="teams"
                  multiple
                  value={inputTeamsValues}
                  onChange={(event, newValue: any) => {
                    setInputTeamsValues(newValue);
                    setFieldValue(
                      'teams',
                      newValue.map((val: any) => val.value)
                    );
                  }}
                  inputValue={inputTeamsDisplayValues}
                  onInputChange={(event, newInputValue) => {
                    setInputTeamsDisplayValues(newInputValue);
                  }}
                  options={teams}
                  getOptionLabel={option => option.description}
                  openOnFocus
                  autoHighlight
                  filterSelectedOptions
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.trim().toLowerCase();
                    if (!input) return options;
                    return options.filter(o => o.description.toLowerCase().includes(input));
                  }}
                  renderOption={(props, option: ILookupType) => {
                    return (
                      <li {...props} key={`${option.value}`}>
                        {option.description}
                      </li>
                    );
                  }}
                  renderInput={params => (
                    <TextField {...params} label={'Teams'} placeholder="Add Teams" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small" error={!!errors?.primaryTeam}>
                <Autocomplete
                  id="primaryTeam"
                  value={inputTeamsValues.find(t => t.value === values.primaryTeam) || null}
                  onChange={(_, newValue: any) => {
                    setFieldValue('primaryTeam', newValue ? newValue.value : '');
                  }}
                  options={inputTeamsValues}
                  getOptionLabel={option => option.description}
                  openOnFocus
                  autoHighlight
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.trim().toLowerCase();
                    if (!input) return options;
                    return options.filter(o => o.description.toLowerCase().includes(input));
                  }}
                  renderOption={(props, option: ILookupType) => (
                    <li {...props} key={`${option.value}`}>{option.description}</li>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={'Primary Team'}
                      error={!!errors?.primaryTeam}
                      placeholder="Search Primary Team"
                    />
                  )}
                />
                {!!errors?.primaryTeam && <FormHelperText>Primary team is required</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Role-specific Dropdowns */}
          {hasSupportTeamFeature ? (
            <>
              {/* Assistant Role - New UI */}
              {employeeBeingEdited.isAssistant && (
                <Grid item container spacing={1} xs={12} className={classes.grid}>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Autocomplete
                        id="assistant-agents"
                        multiple
                        disabled={loadingTeamAdmins}
                        value={assistantAgentsValue}
                        onChange={(event, newValue: any) => {
                          const newAgentIds = newValue.map((val: any) => val.value);
                          // Remove any of these IDs from supportStaffAgents to keep sets disjoint
                          if (values.supportStaffAgents?.length) {
                            const filteredSupport = values.supportStaffAgents.filter((id: number) => !newAgentIds.includes(id));
                            if (filteredSupport.length !== values.supportStaffAgents.length) {
                              setFieldValue('supportStaffAgents', filteredSupport);
                            }
                          }
                          setFieldValue('agents', newAgentIds);
                        }}
                        options={teamAdmins}
                        getOptionLabel={option => option.description}
                        openOnFocus
                        autoHighlight
                        filterSelectedOptions
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        filterOptions={(options, state) => {
                          const input = state.inputValue.trim().toLowerCase();
                          if (!input) return options;
                          return options.filter(o => o.description.toLowerCase().includes(input));
                        }}
                        renderOption={(props, option: ILookupType) => {
                          return (
                            <li {...props} key={`${option.value}`}>
                              {option.description}
                            </li>
                          );
                        }}
                        renderInput={params => (
                          <TextField 
                            {...params} 
                            label={'Assistant to:'} 
                            placeholder="Add Assistant to" 
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              
              {/* Support Team Role - New UI (formerly Support Staff) */}
              {employeeBeingEdited.isSupportStaff && (
                <Grid item container spacing={1} xs={12} className={classes.grid}>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Autocomplete
                        id="support-staff-agents"
                        multiple
                        disabled={loadingTeamAdmins && teamAdmins.length === 0}
                        value={supportStaffAgentsValue}
                        onChange={(event, newValue: any) => {
                          const newAgentIds = newValue.map((val: any) => val.value);
                          // Remove any of these IDs from agents to keep sets disjoint
                          if (values.agents?.length) {
                            const filteredAgents = values.agents.filter((id: number) => !newAgentIds.includes(id));
                            if (filteredAgents.length !== values.agents.length) {
                              setFieldValue('agents', filteredAgents);
                            }
                          }
                          setFieldValue('supportStaffAgents', newAgentIds);
                        }}
                        openOnFocus
                        autoHighlight
                        filterSelectedOptions
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        filterOptions={(options, state) => {
                          const input = state.inputValue.trim().toLowerCase();
                          if (!input) return options;
                          return options.filter(o => o.description.toLowerCase().includes(input));
                        }}
                        options={teamAdmins}
                        getOptionLabel={option => option.description}
                        renderOption={(props, option: ILookupType) => {
                          return (
                            <li {...props} key={`${option.value}`}>
                              {option.description}
                            </li>
                          );
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={'Support Team Member to:'}
                            placeholder={
                              loadingTeamAdmins && teamAdmins.length === 0
                                ? 'Loading…'
                                : 'Add Support Team Member to'
                            }
                            autoComplete="off"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingTeamAdmins && teamAdmins.length === 0 ? (
                                    <span style={{ paddingRight: 4 }}>⏳</span>
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </>
          ) : (
            /* Old UI without feature flag */
            (employeeBeingEdited.isAssistant || employeeBeingEdited.isSupportStaff) && (
              <Grid item container spacing={1} xs={12} className={classes.grid}>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <Autocomplete
                      id="agents"
                      multiple
                      disabled={loadingTeamAdmins}
                      value={agentsValue}
                      onChange={(event, newValue: any) => {
                        const newAgentIds = newValue.map((val: any) => val.value);
                        setFieldValue('agents', newAgentIds);
                      }}
                      inputValue={agentsInput}
                      onInputChange={(event, newInputValue) => {
                        setAgentsInput(newInputValue);
                      }}
                      options={teamAdmins}
                      getOptionLabel={option => option.description}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      filterOptions={(options, state) => {
                        const input = state.inputValue.trim().toLowerCase();
                        if (!input) return options;
                        return options.filter(o => o.description.toLowerCase().includes(input));
                      }}
                      renderOption={(props, option: ILookupType) => {
                        return (
                          <li {...props} key={`${option.value}`}>
                            {option.description}
                          </li>
                        );
                      }}
                      renderInput={params => (
                        <TextField {...params} label={'Agents'} placeholder="Add Agents" />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            )
          )}
          
          {/* Producer Section */}
          {employeeBeingEdited.isProducer && (
            <Grid item container spacing={1} xs={12} className={classes.grid}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    id="primaryAdmin"
                    disabled={loadingAssistants}
                    value={primaryAdminValue}
                    onChange={(_, newValue: any, reason: any) => {
                      if (reason === 'clear' || !newValue) {
                        setFieldValue(
                          'assistants',
                          values.assistants.filter((assistant: any) => !assistant.isPrimary)
                        );
                      } else {
                        setFieldValue('assistants', [
                          ...values.assistants.filter((assistant: any) => !assistant.isPrimary),
                          { employeeId: newValue.value, isPrimary: true },
                        ]);
                      }
                    }}
                    options={assistants.filter(
                      assistant => !additionalAssistantsValue
                        .map(a => a.value)
                        .includes(assistant.value)
                    )}
                    getOptionLabel={option => option.description}
                    openOnFocus
                    autoHighlight
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    filterOptions={(options, state) => {
                      const input = state.inputValue.trim().toLowerCase();
                      if (!input) return options;
                      return options.filter(o => o.description.toLowerCase().includes(input));
                    }}
                    renderOption={(props, option: ILookupType) => (
                      <li {...props} key={`${option.value}`}>{option.description}</li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={'Primary Admin'}
                        placeholder="Add Primary Admin"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    id="AdditionalAdmins"
                    multiple
                    disabled={loadingAssistants}
                    value={additionalAssistantsValue}
                    onChange={(event, newValue: any, reason: any) => {
                      if (reason === 'clear' || !newValue) {
                        setFieldValue(
                          'assistants',
                          values.assistants.filter((assistant: any) => assistant.isPrimary)
                        );
                      } else {
                        const primaryAdmin = values.assistants
                          .find((a: any) => a.isPrimary);
                          
                        setFieldValue(
                          'assistants',
                          [
                            primaryAdmin,
                            ...newValue.map((val: any) => {
                              return { employeeId: val.value, isPrimary: false };
                            }),
                          ].filter(Boolean)
                        );
                      }
                    }}
                    // Uncontrolled input so user can freely type
                    options={assistants.filter(
                      assistant => primaryAdminValue?.value !== assistant.value
                    )}
                    getOptionLabel={option => option.description}
                    openOnFocus
                    autoHighlight
                    filterSelectedOptions
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    filterOptions={(options, state) => {
                      const input = state.inputValue.trim().toLowerCase();
                      if (!input) return options;
                      return options.filter(o => o.description.toLowerCase().includes(input));
                    }}
                    renderOption={(props, option: ILookupType) => {
                      return (
                        <li {...props} key={`${option.value}`}>
                          {option.description}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={hasSupportTeamFeature ? 'Additional Assistants' : 'Additional Admins'}
                        placeholder={hasSupportTeamFeature ? 'Add Additional Assistants' : 'Add Additional Admins'}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    id="SupportStaff"
                    multiple
                    disabled={loadingSupportStaff}
                    value={supportStaffValue}
                    onChange={(event, newValue: any, reason: any) => {
                      if (reason === 'clear' || !newValue) {
                        // Clear support staff
                        setFieldValue('supportStaff', []);
                      } else {
                        setFieldValue(
                          'supportStaff',
                          newValue.map((val: any) => {
                            return { employeeId: val.value };
                          })
                        );
                      }
                    }}
                    // Uncontrolled input restores default typing behavior
                    options={supportStaff}
                    getOptionLabel={option => option.description}
                    openOnFocus
                    autoHighlight
                    filterSelectedOptions
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    filterOptions={(options, state) => {
                      const input = state.inputValue.trim().toLowerCase();
                      if (!input) return options;
                      return options.filter(o => o.description.toLowerCase().includes(input));
                    }}
                    renderOption={(props, option: ILookupType) => {
                      return (
                        <li {...props} key={`${option.value}`}>
                          {option.description}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={hasSupportTeamFeature ? 'Support Team' : 'Support Staff'}
                        placeholder={
                          hasSupportTeamFeature 
                            ? "Add Additional Support Team Member" 
                            : "Add Additional Support Staff"
                        }
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}
          
          {/* Additional Security Section */}
          <Grid item container spacing={1} xs={12} className={classes.grid}>
            <Grid item xs={12}>
              <Typography className={clsx(classes.header, classes.noButton)} variant="body1">
                Additional Security
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.canImport}
                      onChange={() => setFieldValue('canImport', !values.canImport)}
                    />
                  }
                  label="Import"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.canImportPropertyFinancials}
                      onChange={() => setFieldValue('canImportPropertyFinancials', !values.canImportPropertyFinancials)}
                    />
                  }
                  label="Import - Property Financials"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.canExport}
                      onChange={() => setFieldValue('canExport', !values.canExport)}
                    />
                  }
                  label="Export"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.canDeletePerson}
                      onChange={() => setFieldValue('canDeletePerson', !values.canDeletePerson)}
                    />
                  }
                  label="Delete Person"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.canDeleteProperty}
                      onChange={() => setFieldValue('canDeleteProperty', !values.canDeleteProperty)}
                    />
                  }
                  label="Delete Property"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.shouldIncludeInDirectory}
                      onChange={() =>
                        setFieldValue('shouldIncludeInDirectory', !values.shouldIncludeInDirectory)
                      }
                    />
                  }
                  label="Include In Directory"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isDynamicFieldsAdmin}
                      onChange={() =>
                        setFieldValue('isDynamicFieldsAdmin', !values.isDynamicFieldsAdmin)
                      }
                    />
                  }
                  label="Dynamic Fields Admin"
                />
                {showCompanyMergeSetting && 
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.canMergeCompany}
                        onChange={() =>
                          setFieldValue('canMergeCompany', !values.canMergeCompany)
                        }
                      />
                    }
                    label="Company Merge"
                  />
                }
              {hasBulkEmailCreditFeature && (
                <Box display="flex" alignItems="center" gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.canBulkEmail}
                        onChange={() =>
                          setFieldValue('canBulkEmail', !values.canBulkEmail)
                        }
                      />
                    }
                    label="Bulk Emailing"
                  />

                  {values.canBulkEmail && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" component="span">-</Typography>
                      <Typography variant="body2" component="span">SendGrid Credits</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={values.assignedCredit}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || Number(value) < 0) {
                            setFieldValue('assignedCredit', 0);
                          } else {
                            setFieldValue('assignedCredit', Number(value));
                          }
                        }}
                        onBlur={() => {
                          if (!values.assignedCredit || values.assignedCredit < 0) {
                            setFieldValue('assignedCredit', 0);
                          }
                        }}
                        error={touched.assignedCredit && Boolean(errors.assignedCredit)}
                        helperText={touched.assignedCredit && errors.assignedCredit}
                        sx={{ width: '120px' }}
                        inputProps={{ min: 0 }}
                      />
                    </Box>
                  )}
                </Box>
              )}
              </FormGroup>
            </Grid>
          </Grid>
          
          {/* Protected Clients Section */}
          {hasProtectedClients && (
            <Grid item container spacing={1} xs={12} className={classes.grid}>
              <FieldArray name="protectedClients">
                {({ push, remove }) => (
                  <>
                    <Grid item xs={12}>
                      <Typography className={classes.header} variant="body1">
                        Protected Clients
                        <div className={classes.buttonContainer}>
                          <Button
                            type="button"
                            color="secondary"
                            variant="contained"
                            classes={{ root: classes.button }}
                            sx={theme => ({
                              color: '#fff',
                              borderColor: 'rgb(255 255 255 / 50%)',
                              '&:hover': {
                                color: theme.frankie.searchBlue,
                                borderColor: '#ffffff',
                                backgroundColor: '#ffffff',
                              },
                            })}
                            onClick={() =>
                              push({
                                protectedClientToEmployeeId: 0,
                                clientId: undefined,
                                startDate: null,
                                endDate: null,
                                isActive: true,
                              })
                            }
                          >
                            Add
                          </Button>
                        </div>
                      </Typography>
                    </Grid>
                    <Grid item container xs={12}>
                      <Grid item container xs={12} spacing={1} className={classes.grid}>
                        {values?.protectedClients
                          ?.filter((client: any) => !client?.isDeleted)
                          ?.map((client: any, index: number) => {
                            const companyIdValue = `protectedClients[${index}].clientId`;
                            const companyNameValue = `protectedClients[${index}].clientName`;
                            const startDateValue = `protectedClients[${index}].startDate`;
                            const endDateValue = `protectedClients[${index}].endDate`;
                            const isDeleted = `protectedClients[${index}].isDeleted`;
                            return (
                              <Grid key={`${index}`} item container xs={12} spacing={1}>
                                <Grid item xs={12} sm={5}>
                                  <FormControl fullWidth variant="outlined" size="small">
                                    <SearchAheadDropdown
                                      id={`${index}`}
                                      value={{
                                        text: client?.text ?? client?.clientName,
                                        clientId: client.clientId,
                                      }}
                                      onChange={val => {
                                        setFieldValue(companyIdValue, val?.value);
                                        setFieldValue(companyNameValue, val?.text);
                                      }}
                                      label={'Company'}
                                      placeholder={'Company'}
                                    />
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <FormControl fullWidth variant="outlined" size="small">
                                    <DesktopDatePicker
                                      label="Start Date"
                                      inputFormat="MM/dd/yyyy"
                                      value={client.startDate ?? null}
                                      onChange={val => setFieldValue(startDateValue, val)}
                                      renderInput={params => <TextField {...params} />}
                                      // InputProps={{ style: datePickerStyle }}
                                    />
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <FormControl fullWidth variant="outlined" size="small">
                                    <DesktopDatePicker
                                      label="End Date"
                                      inputFormat="MM/dd/yyyy"
                                      value={client.endDate ?? null}
                                      onChange={val => setFieldValue(endDateValue, val)}
                                      renderInput={params => <TextField {...params} />}
                                      // InputProps={{ style: datePickerStyle }}
                                    />
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                  <IconButton
                                    aria-label="remove client"
                                    onClick={() => {
                                      client.protectedClientToEmployeeId === 0
                                        ? remove(index)
                                        : setFieldValue(isDeleted, true);
                                    }}
                                  >
                                    <Close />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </Grid>
                  </>
                )}
              </FieldArray>
            </Grid>
          )}
          
          {/* Margin Categories Section */}
          <Grid item container spacing={1} xs={12} className={classes.grid}>
            <FieldArray name="marginCategories">
              {({ push, remove }) => (
                <>
                  <Grid item xs={12}>
                    <Typography className={classes.header} variant="body1">
                      Margin Categories
                      <div className={classes.buttonContainer}>
                        <Button
                          type="button"
                          color="secondary"
                          variant="contained"
                          classes={{ root: classes.button }}
                          sx={theme => ({
                            color: '#fff',
                            borderColor: 'rgb(255 255 255 / 50%)',
                            '&:hover': {
                              color: theme.frankie.searchBlue,
                              borderColor: '#ffffff',
                              backgroundColor: '#ffffff',
                            },
                          })}
                          onClick={() =>
                            push({
                              employeeMarginCategoryId: 0,
                              marginCategoryTypeId: 0,
                              effectiveDate: null,
                              isDeleted: false,
                            })
                          }
                        >
                          Add
                        </Button>
                      </div>
                    </Typography>
                  </Grid>

                  <Grid item container xs={12}>
                    <Grid item container xs={12} spacing={1} className={classes.grid}>
                      {values?.marginCategories?.map(
                        (category: IEditEmployeeMarginCategory, index: number) => {
                          const marginCategoryTypeId = `marginCategories[${index}].marginCategoryTypeId`;
                          const effectiveDate = `marginCategories[${index}].effectiveDate`;
                          const isDeleted = `marginCategories[${index}].isDeleted`;
                          return (
                            <Grid
                              sx={{ display: category.isDeleted ? 'none' : 'flex' }}
                              key={`${index}-${category.marginCategoryTypeId}`}
                              item
                              container
                              xs={12}
                              spacing={1}
                            >
                              <Grid item xs={12} sm={5}>
                                {/* Margin Categories Type */}
                                <FormControl fullWidth variant="outlined" size="small">
                                  <Dropdowns.Select
                                    label={loadingMarginCategories ? 'Loading...' : 'Type'}
                                    name={marginCategoryTypeId}
                                  >
                                    {marginCategories?.map((category, index) => {
                                      return (
                                        <MenuItem key={`${category.value}`} value={category?.value}>
                                          {category.text}
                                        </MenuItem>
                                      );
                                    })}
                                  </Dropdowns.Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <FormControl fullWidth variant="outlined" size="small">
                                  <DesktopDatePicker
                                    label="Date"
                                    inputFormat="MM/dd/yyyy"
                                    value={category.effectiveDate ?? null}
                                    onChange={val => setFieldValue(effectiveDate, val)}
                                    renderInput={params => <TextField {...params} />}
                                    // InputProps={{ style: datePickerStyle }}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={1}>
                                <IconButton
                                  aria-label="remove client"
                                  onClick={() => {
                                    category.employeeMarginCategoryId === 0
                                      ? remove(index)
                                      : setFieldValue(isDeleted, true);
                                  }}
                                >
                                  <Close />
                                </IconButton>
                              </Grid>
                            </Grid>
                          );
                        }
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
            </FieldArray>
          </Grid>
          
          {/* Action Buttons */}
          <Grid container spacing={1} justifyContent={'flex-end'}>
            <Box marginTop="2rem" marginRight="1rem">
              <Button
                disabled={!dirty || isSubmitting || !isValid}
                type="submit"
                startIcon={<Save />}
                color="secondary"
                variant="contained"
              >
                Save
              </Button>
            </Box>
            <Box marginTop="2rem">
              <Button
                onClick={handleClose}
                startIcon={<Close />}
                color="primary"
                variant="contained"
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Form>
      </Fade>
    </Modal>
  );
};

const useStyles = makeStyles<Theme>(() => ({
  emphasis: {
    fontWeight: 600,
    color: theme.palette.secondary.main,
  },

  label: {
    backgroundColor: theme.palette.background.paper,
  },
  grid: {
    marginTop: theme.spacing(0.5),
    paddingTop: '0 !important',
  },
  readonly: {
    paddingTop: theme.spacing(0.5),
  },

  header: {
    backgroundColor: '#005570',
    color: '#eff4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.25rem 1rem',
    fontWeight: 600,
  },
  noButton: {
    padding: '.704rem 1rem',
  },
  button: {
    border: '1px solid rgba(255, 255, 255, 0.5)',
    color: '#fff',
    '&:hover': {
      color: theme.frankie.searchBlue,
      borderColor: '#ffffff',
      backgroundColor: '#ffffff',
    },
  },
  resetButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
}));
